import { BaseAdapter } from "./base-adapter";
import {
  TEvent,
  EventType,
  IPageViewEvent,
  IButtonClickEvent,
  IFormSubmissionEvent,
  IProductViewEvent,
  IAddToCartEvent,
  IRemoveFromCartEvent,
  ICheckoutStartedEvent,
  ICheckoutCompletedEvent,
  ISearchEvent,
  IUserSignupEvent,
  IUserLoginEvent,
  ICustomEvent,
} from "../types";
import { EventNames, DEFAULT_CURRENCY } from "../constants";

declare global {
  interface Window {
    Moengage?: {
      track_event: (
        eventName: string,
        properties?: Record<string, any>
      ) => void;
      add_user_attribute: (key: string, value: any) => void;
      add_first_name: (firstName: string) => void;
      add_last_name: (lastName: string) => void;
      add_email: (email: string) => void;
      add_mobile: (mobile: string) => void;
      add_user_name: (username: string) => void;
      add_gender: (gender: string) => void;
      add_birthday: (birthday: Date) => void;
      add_unique_user_id: (userId: string) => void;
    };
  }
}

/**
 * Configuration for the MoenGage adapter
 */
export interface MoengageAdapterConfig {
  /**
   * Whether to enable user identification
   */
  enableUserIdentification?: boolean;
}

/**
 * MoenGage tracking adapter
 * Assumes that the MoenGage script is already loaded in the page
 */
export class MoengageAdapter extends BaseAdapter {
  public readonly name = "moengage";

  /**
   * Constructor
   * @param config Configuration for the adapter
   */
  constructor(config: MoengageAdapterConfig = {}) {
    super(config);
  }

  /**
   * Initialize the MoenGage adapter
   * This just checks if MoenGage is available
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // Skip initialization if running on server
    if (typeof window === "undefined") {
      return;
    }

    // Check if MoenGage is available
    if (!window.Moengage) {
      console.warn("MoenGage not found. Make sure the script is loaded.");
      return;
    }

    this.initialized = true;
  }

  /**
   * Track an event with MoenGage
   * @param event The event to track
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (
      !this.initialized ||
      typeof window === "undefined" ||
      !window.Moengage
    ) {
      return;
    }

    switch (event.type) {
      case EventType.PAGE_VIEW:
        this.trackPageView(event as IPageViewEvent);
        break;
      case EventType.BUTTON_CLICK:
        this.trackButtonClick(event as IButtonClickEvent);
        break;
      case EventType.FORM_SUBMISSION:
        this.trackFormSubmission(event as IFormSubmissionEvent);
        break;
      case EventType.PRODUCT_VIEW:
        this.trackProductView(event as IProductViewEvent);
        break;
      case EventType.ADD_TO_CART:
        this.trackAddToCart(event as IAddToCartEvent);
        break;
      case EventType.REMOVE_FROM_CART:
        this.trackRemoveFromCart(event as IRemoveFromCartEvent);
        break;
      case EventType.CHECKOUT_STARTED:
        this.trackCheckoutStarted(event as ICheckoutStartedEvent);
        break;
      case EventType.CHECKOUT_COMPLETED:
        this.trackCheckoutCompleted(event as ICheckoutCompletedEvent);
        break;
      case EventType.SEARCH:
        this.trackSearch(event as ISearchEvent);
        break;
      case EventType.USER_SIGNUP:
        this.trackUserSignup(event as IUserSignupEvent);
        break;
      case EventType.USER_LOGIN:
        this.trackUserLogin(event as IUserLoginEvent);
        break;
      case EventType.CUSTOM:
        this.trackCustomEvent(event as ICustomEvent);
        break;
      default:
        // For other events, track as custom events
        window.Moengage.track_event(event.type, this.eventToParams(event));
        break;
    }
  }

  /**
   * Track a page view event
   * @param event The page view event
   */
  private trackPageView(event: IPageViewEvent): void {
    window.Moengage!.track_event(EventNames.PAGE_VIEWED, {
      page_path: event.path,
      page_title: event.title,
      page_url: window.location.href,
      referrer: event.referrer || document.referrer,
    });
  }

  /**
   * Track a button click event
   * @param event The button click event
   */
  private trackButtonClick(event: IButtonClickEvent): void {
    window.Moengage!.track_event(EventNames.BUTTON_CLICKED, {
      button_id: event.buttonId,
      button_text: event.buttonText,
      location: event.location,
    });
  }

  /**
   * Track a form submission event
   * @param event The form submission event
   */
  private trackFormSubmission(event: IFormSubmissionEvent): void {
    window.Moengage!.track_event(EventNames.FORM_SUBMITTED, {
      form_id: event.formId,
      form_name: event.formName,
      success: event.success,
    });
  }

  /**
   * Track a product view event
   * @param event The product view event
   */
  private trackProductView(event: IProductViewEvent): void {
    window.Moengage!.track_event(EventNames.PRODUCT_VIEWED, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      category: event.category,
    });
  }

  /**
   * Track an add to cart event
   * @param event The add to cart event
   */
  private trackAddToCart(event: IAddToCartEvent): void {
    window.Moengage!.track_event(EventNames.ADDED_TO_CART, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      quantity: event.quantity,
      variant: event.variant,
      total_value: event.price * event.quantity,
    });
  }

  /**
   * Track a remove from cart event
   * @param event The remove from cart event
   */
  private trackRemoveFromCart(event: IRemoveFromCartEvent): void {
    window.Moengage!.track_event(EventNames.REMOVED_FROM_CART, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      quantity: event.quantity,
      variant: event.variant,
      total_value: event.price * event.quantity,
    });
  }

  /**
   * Track a checkout started event
   * @param event The checkout started event
   */
  private trackCheckoutStarted(event: ICheckoutStartedEvent): void {
    window.Moengage!.track_event(EventNames.CHECKOUT_STARTED, {
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Track a checkout completed event
   * @param event The checkout completed event
   */
  private trackCheckoutCompleted(event: ICheckoutCompletedEvent): void {
    window.Moengage!.track_event(EventNames.PURCHASE_COMPLETED, {
      order_id: event.orderId,
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Track a search event
   * @param event The search event
   */
  private trackSearch(event: ISearchEvent): void {
    window.Moengage!.track_event(EventNames.SEARCH, {
      search_term: event.searchTerm,
      results_count: event.resultsCount,
    });
  }

  /**
   * Track a user signup event
   * @param event The user signup event
   */
  private trackUserSignup(event: IUserSignupEvent): void {
    window.Moengage!.track_event(EventNames.USER_SIGNED_UP, {
      user_id: event.userId,
      method: event.method,
      success: event.success,
    });

    // Add user attributes if user ID is available and user identification is enabled
    if (
      event.userId &&
      this.getConfig<boolean>("enableUserIdentification", true)
    ) {
      window.Moengage!.add_unique_user_id(event.userId);
    }
  }

  /**
   * Track a user login event
   * @param event The user login event
   */
  private trackUserLogin(event: IUserLoginEvent): void {
    window.Moengage!.track_event(EventNames.USER_LOGGED_IN, {
      user_id: event.userId,
      method: event.method,
      success: event.success,
    });

    // Add user attributes if user ID is available and user identification is enabled
    if (
      event.userId &&
      this.getConfig<boolean>("enableUserIdentification", true)
    ) {
      window.Moengage!.add_unique_user_id(event.userId);
    }
  }

  /**
   * Track a custom event
   * @param event The custom event
   */
  private trackCustomEvent(event: ICustomEvent): void {
    window.Moengage!.track_event(event.name, event.properties);
  }

  /**
   * Convert an event to MoenGage parameters
   * @param event The event to convert
   */
  private eventToParams(event: TEvent): Record<string, any> {
    // Remove type and timestamp from the event
    const { type, timestamp, ...params } = event;
    return params;
  }

  /**
   * Set user attributes in MoenGage
   * @param attributes User attributes to set
   */
  public setUserAttributes(attributes: Record<string, any>): void {
    if (
      !this.initialized ||
      typeof window === "undefined" ||
      !window.Moengage
    ) {
      return;
    }

    // Set user attributes
    Object.entries(attributes).forEach(([key, value]) => {
      switch (key) {
        case "firstName":
          window.Moengage!.add_first_name(value as string);
          break;
        case "lastName":
          window.Moengage!.add_last_name(value as string);
          break;
        case "email":
          window.Moengage!.add_email(value as string);
          break;
        case "mobile":
          window.Moengage!.add_mobile(value as string);
          break;
        case "username":
          window.Moengage!.add_user_name(value as string);
          break;
        case "gender":
          window.Moengage!.add_gender(value as string);
          break;
        case "birthday":
          window.Moengage!.add_birthday(value as Date);
          break;
        case "userId":
          window.Moengage!.add_unique_user_id(value as string);
          break;
        default:
          window.Moengage!.add_user_attribute(key, value);
          break;
      }
    });
  }
}
